import { ApplicationInsights } from '@microsoft/applicationinsights-web'
import type { App } from 'vue'

/**
 * Application Insights plugin for Vue 3.
 *
 * Initializes the SDK, provides the instance for injection,
 * and automatically tracks the authenticated user from the
 * utilisateur Pinia store.
 *
 * Requires: VITE_MS_INSIGHTS_CONNECTION_STRING env var.
 */

const APP_INSIGHTS_KEY = Symbol('appInsights')

let appInsightsInstance: ApplicationInsights | null = null

export { APP_INSIGHTS_KEY }

/** Inject the AppInsights instance anywhere (e.g. for custom tracking). */
export function useAppInsights(): ApplicationInsights | null {
  return appInsightsInstance
}

/** Exposed for callers outside component setup (e.g. InitialLoading). */
export function setAppInsightsUser(userId: string): void {
  appInsightsInstance?.setAuthenticatedUserContext(userId)
}

export function clearAppInsightsUser(): void {
  appInsightsInstance?.clearAuthenticatedUserContext()
}

export default {
  install(app: App) {
    const connectionString = import.meta.env.VITE_MS_INSIGHTS_CONNECTION_STRING

    if (!connectionString) {
      console.warn('[AppInsights] VITE_MS_INSIGHTS_CONNECTION_STRING not set — skipping')
      return
    }

    const appInsights = new ApplicationInsights({
      config: {
        connectionString,
        enableAutoRouteTracking: true,
        enableUnhandledPromiseRejectionTracking: true,
      },
    })

    appInsights.loadAppInsights()
    appInsights.trackPageView()

    appInsightsInstance = appInsights
    app.provide(APP_INSIGHTS_KEY, appInsights)

    // ── Auto-track authenticated user from Pinia store ──
    // Deferred to next microtask so Pinia is guaranteed to be
    // installed (plugins are loaded alphabetically, and
    // 'applicationinsights' < 'pinia' in the glob).
    app.runWithContext(() => {
      Promise.resolve().then(() => {
        const storeU = useUtilisateurStore()
        let previousUserId: string | undefined

        storeU.$subscribe((_mutation, state) => {
          const newId = state.utilisateur?.nomUtilisateur
          if (newId !== previousUserId) {
            previousUserId = newId
            if (newId) {
              appInsights.setAuthenticatedUserContext(newId)
            } else {
              appInsights.clearAuthenticatedUserContext()
            }
          }
        })

        // ── Track site selection ──
        let previousSiteLd: string | undefined

        storeU.$subscribe((_mutation, state) => {
          const newSite = state.siteSélectionné?.lettresDappel
          if (newSite && newSite !== previousSiteLd) {
            previousSiteLd = newSite
            appInsights.trackEvent({
              name: 'SiteSelected',
              properties: { lettresDappel: newSite },
            })
          }
        })
      })
    })
  },
}